import { Info } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { SummaryDetailLevel } from '@/client/types.gen'
import { Button as UIButton } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'

import { SUMMARY_DETAIL_LEVEL_SELECT_ORDER } from './summaryDetailLevelSelectOrder'
import type { StepComponentProps } from './types'

interface SummaryOptionsStepProps extends StepComponentProps {
	summaryDetailLevel: SummaryDetailLevel
	onSummaryDetailLevelChange: (level: SummaryDetailLevel) => void
	generationUserInstructions: string
	onGenerationUserInstructionsChange: (value: string) => void
	/** Mount Select dropdown inside the modal overlay to avoid body portal teardown races. */
	portalContainer?: HTMLElement | null
	/** Parent can await a clean Select close before unmounting this step (back/close). */
	registerCloseSelect?: (close: (() => Promise<void>) | null) => void
}

export function SummaryOptionsStep({
	summaryDetailLevel,
	onSummaryDetailLevelChange,
	generationUserInstructions,
	onGenerationUserInstructionsChange,
	onContinue,
	portalContainer,
	registerCloseSelect,
}: SummaryOptionsStepProps) {
	const { t } = useTranslation()
	const [selectOpen, setSelectOpen] = useState(false)
	const selectOpenRef = useRef(selectOpen)
	selectOpenRef.current = selectOpen
	const pendingContinueRef = useRef(false)
	const pendingCloseResolveRef = useRef<(() => void) | null>(null)

	const closeSelect = useCallback((): Promise<void> => {
		if (!selectOpenRef.current) return Promise.resolve()
		return new Promise((resolve) => {
			pendingCloseResolveRef.current = resolve
			setSelectOpen(false)
		})
	}, [])

	useEffect(() => {
		registerCloseSelect?.(closeSelect)
		return () => registerCloseSelect?.(null)
	}, [closeSelect, registerCloseSelect])

	const handleOpenChange = (open: boolean) => {
		setSelectOpen(open)
		if (!open) {
			pendingCloseResolveRef.current?.()
			pendingCloseResolveRef.current = null
			if (pendingContinueRef.current) {
				pendingContinueRef.current = false
				onContinue?.()
			}
		}
	}

	const handleContinue = () => {
		if (selectOpen) {
			pendingContinueRef.current = true
			setSelectOpen(false)
			return
		}
		onContinue?.()
	}

	const detailLevelOptions = SUMMARY_DETAIL_LEVEL_SELECT_ORDER.map((level) => ({
		level,
		label: t(`pages.summaryDetailLevel.options.${level}.label` as never),
	}))

	return (
		<div className="flex-1 flex flex-col min-h-0 space-y-4">
			<div>
				<div className="flex items-center gap-2 mb-1.5">
					<h2 className="text-lg font-semibold text-foreground">
						{t('pages.projectDetail.modal.summaryOptionsStep.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
						</TooltipTrigger>
						<TooltipContent className="max-w-sm text-left">
							<p className="text-pretty">{t('pages.projectDetail.modal.summaryOptionsStep.tooltip')}</p>
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto overflow-x-hidden px-1 py-1">
				<div className="p-3 space-y-2">
					<Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						{t('pages.summaryDetailLevel.labelModal')}
					</Label>
					<Select
						open={selectOpen}
						onOpenChange={handleOpenChange}
						value={summaryDetailLevel}
						onValueChange={(value) => onSummaryDetailLevelChange(value as SummaryDetailLevel)}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder={t('pages.summaryDetailLevel.selectPlaceholder')} />
						</SelectTrigger>
						<SelectContent container={portalContainer ?? undefined}>
							{detailLevelOptions.map(({ level, label }) => (
								<SelectItem key={level} value={level}>
									{label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						{t(`pages.summaryDetailLevel.options.${summaryDetailLevel}.description` as never)}
					</p>
				</div>

				<div className="p-3 space-y-2 border-t border-border">
					<Label
						htmlFor="generation-user-instructions"
						className="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
					>
						{t('pages.projectDetail.modal.summaryOptionsStep.customInstructionsLabel')}
					</Label>
					<Textarea
						id="generation-user-instructions"
						value={generationUserInstructions}
						onChange={(event) => onGenerationUserInstructionsChange(event.target.value)}
						placeholder={t('pages.projectDetail.modal.summaryOptionsStep.customInstructionsPlaceholder')}
						rows={4}
						maxLength={4000}
						className="resize-y min-h-24"
					/>
				</div>
			</div>

			<div className="flex gap-2">
				<UIButton
					type="button"
					onClick={handleContinue}
					className="flex-[2]"
					data-tour={TOUR.modalSummaryContinue}
				>
					{t('common.continue')}
				</UIButton>
			</div>
		</div>
	)
}
